"use client";

// useLiveMemory — the organism's nervous system.
//
// The watercolour bloom IS a user's memory. Whenever memory changes anywhere in
// the app, the organism should FIRE its neurons (a pulse + a stream travelling
// the lattice). This hook is the single source of those firings. It listens on
// two channels and folds both into one monotonic `pulse` counter that
// MemoryOrganism consumes (its existing `pulse` prop):
//
//   (a) IN-APP EVENT BUS — a window CustomEvent `innernet:memory`. Any client
//       mutation (capture / commit / save / refine …) can call emitMemoryEvent()
//       and the organism fires. Zero coupling: the dispatcher and the listener
//       only share the event name. Call sites elsewhere are optional — at
//       minimum the bus + listener exist here and fire.
//
//   (b) SUPABASE REALTIME — a subscription to the signed-in user's INSERTs on
//       the live feed table. `activity_events` is the natural one: every agent
//       action writes a row there (and it carries a `user_id` column), so a
//       capture from Claude Desktop, a commit from the CLI, or a save from the
//       web all light up the organism even when the mutation didn't originate in
//       this tab. We filter by user_id on the channel; RLS additionally gates it.
//       (Realtime must be enabled on the table server-side — see FEED_TABLES.)
//
// Bursts are DEBOUNCED into a flurry: many rapid events coalesce so the organism
// reads as excited, never as a seizure/strobe. Reduced-motion still fires (the
// organism should react), just at a calmer cadence handled downstream.

import { useEffect, useRef, useState } from "react";
import { createClient } from "../supabase/client";

/** The window CustomEvent the in-app bus dispatches/listens on. */
export const MEMORY_EVENT = "innernet:memory";

export type MemoryEventDetail = {
  /** What changed, for downstream tuning (e.g. a commit could fire harder). */
  kind?: "capture" | "commit" | "save" | "node" | "self" | "refine" | string;
  /** How many discrete changes, so a bulk write can read as a bigger flurry. */
  count?: number;
  /** Free-form payload (table row, id, …) — passed through, never required. */
  [k: string]: unknown;
};

/**
 * IN-APP EVENT BUS — dispatch a memory-change event. Any client-side mutation
 * (capture / commit / save / refine) can call this and the live organism fires.
 * Safe to call from anywhere; a no-op during SSR.
 */
export function emitMemoryEvent(detail: MemoryEventDetail = {}): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent<MemoryEventDetail>(MEMORY_EVENT, { detail }));
  } catch {
    /* CustomEvent unsupported (ancient env) — ignore; the organism just won't fire */
  }
}

// The live feed tables we watch by default. `activity_events` is the canonical
// one — every agent action (capture / commit / save / sync from any client)
// writes a row there, AND it carries a `user_id` column so we can filter the
// realtime stream to just this user's rows. (`commits` and `nodes` are scoped by
// `project_id`, NOT `user_id`, so they can't be user-filtered here without a
// schema change; pass them via `opts.tables` with the right column if you enable
// realtime + a user-scoped filter on them later.)
//
// NOTE (server-side): realtime must be enabled on the table for this to deliver.
// `activity_events` IS in the `supabase_realtime` publication (migrations
// enable_realtime_activity_events + realtime_live), so this subscription
// delivers live cross-client INSERTs; the in-app event bus additionally fires
// the organism on local mutations in this tab.
const FEED_TABLES: Array<{ table: string; userCol: string }> = [
  { table: "activity_events", userCol: "user_id" },
];

// Coalesce a burst of events into one flurry. A window this short still lets a
// rapid sequence (e.g. a sync writing 20 rows) read as a single excited flurry
// rather than 20 separate jolts.
const DEBOUNCE_MS = 220;

export type LiveMemoryState = {
  /** Monotonic counter — bump => fire. Feed straight into MemoryOrganism.pulse. */
  pulse: number;
  /** How many raw events folded into the LAST pulse (1 = single, N = flurry).
   *  Lets the organism fire proportionally (a bigger flurry → a bigger swell). */
  burst: number;
};

type Options = {
  /** Disable the Supabase realtime path (event bus only). Default: subscribe. */
  realtime?: boolean;
  /** Extra tables to watch beyond the defaults. */
  tables?: Array<{ table: string; userCol: string }>;
};

/**
 * Subscribe to memory changes and surface a `pulse` counter for the organism.
 * MemoryOrganism mounts this itself, so the organism fires with no wiring at the
 * call sites. Returns { pulse, burst }.
 */
export function useLiveMemory(opts: Options = {}): LiveMemoryState {
  const realtime = opts.realtime ?? true;
  const [state, setState] = useState<LiveMemoryState>({ pulse: 0, burst: 0 });
  // pending coalesce window: how many raw events have arrived since the last
  // flush, and the timer that will flush them into a single pulse.
  const pending = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let disposed = false;

    const flush = () => {
      timer.current = null;
      const n = pending.current;
      pending.current = 0;
      if (n <= 0 || disposed) return;
      // one pulse per flurry; `burst` reports how many events it folded.
      setState((s) => ({ pulse: s.pulse + 1, burst: n }));
    };

    // a raw event arrived → add to the pending flurry and (re)arm the flush.
    const onEvent = (count = 1) => {
      pending.current += Math.max(1, count);
      if (timer.current == null) timer.current = setTimeout(flush, DEBOUNCE_MS);
    };

    // (a) in-app event bus
    const onMemory = (e: Event) => {
      const detail = (e as CustomEvent<MemoryEventDetail>).detail ?? {};
      onEvent(typeof detail.count === "number" ? detail.count : 1);
    };
    window.addEventListener(MEMORY_EVENT, onMemory as EventListener);

    // (b) Supabase realtime — scoped to the signed-in user's INSERTs. If the
    // table doesn't have realtime enabled server-side, the subscription simply
    // never delivers (no error, no-op) and the event bus still drives firing.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let channel: any = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let supabase: any = null;
    if (realtime) {
      (async () => {
        try {
          supabase = createClient();
          const { data } = await supabase.auth.getUser();
          const uid = data.user?.id;
          if (disposed || !uid) return; // signed out → event bus only
          const tables = [...FEED_TABLES, ...(opts.tables ?? [])];
          channel = supabase.channel(`organism-live-${uid}`);
          for (const { table, userCol } of tables) {
            channel.on(
              "postgres_changes",
              { event: "INSERT", schema: "public", table, filter: `${userCol}=eq.${uid}` },
              () => onEvent(1)
            );
          }
          channel.subscribe();
        } catch {
          /* no supabase env / not signed in — event bus path remains live */
        }
      })();
    }

    return () => {
      disposed = true;
      window.removeEventListener(MEMORY_EVENT, onMemory as EventListener);
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      if (channel) {
        try {
          // tear the channel down via the SAME client that opened it.
          if (supabase) supabase.removeChannel(channel);
          else channel.unsubscribe();
        } catch {
          /* ignore */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realtime]);

  return state;
}
